/**
 * Tiny pure helpers for merging client + server validation errors and wiring
 * accessible aria attributes on the offending input.
 *
 * Forms keep their own `validationErrors: $state(...)` map. These two
 * functions cover the boilerplate that would otherwise repeat in every
 * sheet/form. No store, no rune wrapper — just merge + aria.
 */

export type FieldErrors = Record<string, string>;

/**
 * Merge client-side validation errors with server-side `fieldErrors`.
 * Server messages win on conflict because the server has the authoritative
 * view of what's actually wrong with the submission. Client-only keys
 * (e.g. local-only checks not run server-side) are preserved untouched.
 */
export function mergeFieldErrors(
	clientErrors: FieldErrors,
	serverFieldErrors: FieldErrors
): FieldErrors {
	return { ...clientErrors, ...serverFieldErrors };
}

/**
 * Aria attributes for an input bound to a given form field. Returns an
 * empty object when the field has no error so the spread doesn't pollute
 * the DOM with `aria-invalid="false"` (which screen readers may still
 * announce).
 */
export function ariaInputProps(
	field: string,
	errors: FieldErrors,
	errorIdPrefix = 'err'
): { 'aria-invalid'?: 'true'; 'aria-describedby'?: string } {
	if (!errors[field]) return {};
	return {
		'aria-invalid': 'true',
		'aria-describedby': `${errorIdPrefix}-${field}`
	};
}

/** Stable id for the inline `<p role="alert">` paired with the input. */
export function fieldErrorId(field: string, errorIdPrefix = 'err'): string {
	return `${errorIdPrefix}-${field}`;
}

/**
 * Duck-typed check for an error that carries per-field validation messages.
 * Avoids forcing every form component to import the `ApiError` class — the
 * structural shape is the contract; the class is an implementation detail.
 * Returns `true` only when there's at least one field error to surface.
 */
export function hasFieldErrors(err: unknown): err is { fieldErrors: Record<string, string> } {
	if (typeof err !== 'object' || err === null) return false;
	if (!('fieldErrors' in err)) return false;
	const fe = (err as { fieldErrors: unknown }).fieldErrors;
	if (typeof fe !== 'object' || fe === null) return false;
	return Object.keys(fe as object).length > 0;
}
